import { Component, OnDestroy, OnInit } from '@angular/core'
import { collection, getDocs, onSnapshot, query, where, Unsubscribe } from '@angular/fire/firestore'
import { AdminClient, CrmAssignee, ContractStatus, FirestoreStructure } from '@parkupp/core'
import { AdminService } from 'src/app/services/admin.service'
import { AuthenticationService } from 'src/app/services/authentication.service'

export interface ExpiringLeaseRow {
    adminClient: AdminClient
    subscriptionKey: string | null
    summary: {
        status: string
        startDate: any
        endDate: any
        period: number
        baysBooked: number
        createdAt: any
        renewalStatus: string | null
        nonRenewalReason: string | null
        nonRenewalNote: string | null
        denormParkingTitle: string | null
        feeInCents: number
    }
    daysUntilExpiry: number
}

@Component({
    selector: 'app-expiring-leases',
    templateUrl: './expiring-leases.component.html',
    styleUrls: ['./expiring-leases.component.scss'],
})
export class ExpiringLeasesComponent implements OnInit, OnDestroy {
    searchReady = false
    view: 'table' | 'stats' = 'table'

    crmAssignees: CrmAssignee[] = []
    crmAssigneeMap: { [key: string]: CrmAssignee } = {}

    expiringRows: ExpiringLeaseRow[] = []
    expiredRows: ExpiringLeaseRow[] = []
    allExpiredRows: ExpiringLeaseRow[] = []
    statsExpiredRows: ExpiringLeaseRow[] = []

    expiryWindowDays = parseInt(localStorage.getItem('expiringLeasesWindowDays') || '7', 10)
    expiredLookbackDays = 365

    statsLookbackDays = parseInt(localStorage.getItem('expiringLeasesStatsLookbackDays') || '30', 10)
    statsLookbackOptions = [
        { value: 30, label: '30 days' },
        { value: 60, label: '60 days' },
        { value: 90, label: '90 days' },
        { value: 180, label: '180 days' },
        { value: 365, label: '365 days' },
    ]

    expiryWindowOptions = [
        { value: 7, label: '7 days' },
        { value: 14, label: '14 days' },
        { value: 30, label: '30 days' },
        { value: 60, label: '60 days' },
        { value: 90, label: '90 days' },
    ]

    isSalesManagerMode = false

    private clientsUnsubscribe: Unsubscribe | null = null

    constructor(
        private adminSvc: AdminService,
        private authService: AuthenticationService
    ) {}

    ngOnInit(): void {
        this.isSalesManagerMode = this.authService.isSalesManager && !this.authService.isAdmin

        this.loadCrmAssignees()
        this.loadData()
    }

    ngOnDestroy(): void {
        if (this.clientsUnsubscribe) {
            this.clientsUnsubscribe()
            this.clientsUnsubscribe = null
        }
    }

    async loadCrmAssignees(): Promise<void> {
        const snapshot = await getDocs(
            query(collection(this.adminSvc.firestore, FirestoreStructure.CRM_ASSIGNEES), where('active', '==', true))
        )
        this.crmAssignees = snapshot.docs.map((doc) => new CrmAssignee(doc.data(), doc.id))
        this.crmAssigneeMap = {}
        this.crmAssignees.forEach((assignee) => {
            if (assignee.$key) {
                this.crmAssigneeMap[assignee.$key] = assignee
            }
        })
    }

    loadData(): void {
        if (this.clientsUnsubscribe) {
            this.clientsUnsubscribe()
            this.clientsUnsubscribe = null
        }

        const clientsRef = query(
            collection(this.adminSvc.firestore, FirestoreStructure.ADMIN_CLIENTS),
            where('hasEverHadActiveOrCompletedSubscription', '==', true)
        )

        this.clientsUnsubscribe = onSnapshot(clientsRef, (snapshot) => {
            const now = new Date()
            const expiredCutoff = new Date(now.getTime() - this.expiredLookbackDays * 24 * 60 * 60 * 1000)

            const newExpiringRows: ExpiringLeaseRow[] = []
            const newExpiredRows: ExpiringLeaseRow[] = []

            snapshot.docs.forEach((doc) => {
                const data = doc.data()
                const adminClient = new AdminClient({ ...data, $key: doc.id })
                const summaries = data['subscriptionSummaries'] as Record<string, any> | undefined

                if (summaries && Object.keys(summaries).length > 0) {
                    const hasAnActiveSub = Object.values(summaries).some(
                        (s: any) => s.status === ContractStatus.active
                    )

                    Object.entries(summaries).forEach(([subKey, summary]: [string, any]) => {
                        const endDate = this.toDate(summary.endDate)
                        if (!endDate) return

                        const daysUntilExpiry = Math.ceil((endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))

                        const row: ExpiringLeaseRow = {
                            adminClient,
                            subscriptionKey: subKey,
                            summary: {
                                status: summary.status || '',
                                startDate: summary.startDate,
                                endDate: summary.endDate,
                                period: summary.period || 0,
                                baysBooked: summary.baysBooked || 0,
                                createdAt: summary.createdAt,
                                renewalStatus: summary.renewalStatus || null,
                                nonRenewalReason: summary.nonRenewalReason || null,
                                nonRenewalNote: summary.nonRenewalNote || null,
                                denormParkingTitle: summary.denormParkingTitle || null,
                                feeInCents: summary.feeInCents || 0,
                            },
                            daysUntilExpiry,
                        }

                        const isActive = summary.status === ContractStatus.active
                        const isExpired = [ContractStatus.completed, ContractStatus.expired, ContractStatus.clientBreachedNonPayment, ContractStatus.mutuallyTerminated].includes(summary.status)

                        if (isActive && daysUntilExpiry >= 0 && daysUntilExpiry <= this.expiryWindowDays) {
                            newExpiringRows.push(row)
                        } else if (isExpired && !hasAnActiveSub && endDate >= expiredCutoff && endDate <= now) {
                            newExpiredRows.push(row)
                        }
                    })
                } else {
                    // Fallback: use top-level AdminClient fields (activeSubscriptionExpiry, etc.)
                    const expiry = this.toDate(data['activeSubscriptionExpiry'])

                    const clientNonRenewalReason = data['nonRenewalReason'] || null

                    if (adminClient.hasActiveSubscription && expiry) {
                        const daysUntilExpiry = Math.ceil((expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))

                        if (daysUntilExpiry >= 0 && daysUntilExpiry <= this.expiryWindowDays) {
                            newExpiringRows.push({
                                adminClient,
                                subscriptionKey: null,
                                summary: {
                                    status: ContractStatus.active,
                                    startDate: null,
                                    endDate: expiry,
                                    period: adminClient.monthsRequired || 0,
                                    baysBooked: adminClient.numberOfBays || 0,
                                    createdAt: null,
                                    renewalStatus: null,
                                    nonRenewalReason: clientNonRenewalReason,
                                    nonRenewalNote: null,
                                    denormParkingTitle: null,
                                    feeInCents: 0,
                                },
                                daysUntilExpiry,
                            })
                        }
                    } else if (!adminClient.hasActiveSubscription && adminClient.hasEverHadActiveOrCompletedSubscription) {
                        const lastExpiry = this.toDate(data['activeSubscriptionExpiry'])
                        if (lastExpiry && lastExpiry >= expiredCutoff && lastExpiry <= now) {
                            const daysUntilExpiry = Math.ceil((lastExpiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
                            newExpiredRows.push({
                                adminClient,
                                subscriptionKey: null,
                                summary: {
                                    status: ContractStatus.completed,
                                    startDate: null,
                                    endDate: lastExpiry,
                                    period: adminClient.monthsRequired || 0,
                                    baysBooked: adminClient.numberOfBays || 0,
                                    createdAt: null,
                                    renewalStatus: null,
                                    nonRenewalReason: clientNonRenewalReason,
                                    nonRenewalNote: null,
                                    denormParkingTitle: null,
                                    feeInCents: 0,
                                },
                                daysUntilExpiry,
                            })
                        }
                    }
                }
            })

            this.expiringRows = newExpiringRows.sort((a, b) => a.daysUntilExpiry - b.daysUntilExpiry)
            this.allExpiredRows = newExpiredRows.sort((a, b) => b.daysUntilExpiry - a.daysUntilExpiry)

            const tableExpiredCutoff = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000)
            this.expiredRows = this.allExpiredRows.filter((r) => {
                const endDate = this.toDate(r.summary.endDate)
                return endDate && endDate >= tableExpiredCutoff
            })

            this.computeStatsExpiredRows()
            this.searchReady = true

            console.log(`[Expiring Leases] ${snapshot.docs.length} clients -> ${newExpiringRows.length} expiring, ${this.expiredRows.length} expired (table), ${this.allExpiredRows.length} expired (total)`)
        })
    }

    onExpiryWindowChange(): void {
        localStorage.setItem('expiringLeasesWindowDays', this.expiryWindowDays.toString())
        this.loadData()
    }

    onStatsLookbackChange(): void {
        localStorage.setItem('expiringLeasesStatsLookbackDays', this.statsLookbackDays.toString())
        this.computeStatsExpiredRows()
    }

    computeStatsExpiredRows(): void {
        const now = new Date()
        const cutoff = new Date(now.getTime() - this.statsLookbackDays * 24 * 60 * 60 * 1000)
        this.statsExpiredRows = this.allExpiredRows.filter((r) => {
            const endDate = this.toDate(r.summary.endDate)
            return endDate && endDate >= cutoff
        })
    }

    getAssigneeDisplayName(userResponsible: string | undefined): string {
        if (!userResponsible) return '-'
        const assignee = this.crmAssigneeMap[userResponsible]
        return assignee ? assignee.displayName : '-'
    }

    private toDate(value: any): Date | null {
        if (!value) return null
        if (value instanceof Date) return value
        if (typeof value.toDate === 'function') return value.toDate()
        if (value.seconds) return new Date(value.seconds * 1000)
        if (typeof value === 'string') return new Date(value)
        return null
    }
}
